import { X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import { formatCount } from '@/utils/number';
import { PERCENT_SCALE } from '../constants/teacherSubmission.constants';
import type { TeacherDetail } from '../types/teacherSubmission.types';
import { getProfileCompleteness } from '../utils/profileCompleteness';

const RING_VIEWBOX_SIZE = 56;
const RING_STROKE_WIDTH = 5;
const RING_RADIUS = (RING_VIEWBOX_SIZE - RING_STROKE_WIDTH) / 2;
const RING_CENTER = RING_VIEWBOX_SIZE / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_TRACK_OPACITY = 0.15;
const HEALTHY_PERCENT = 80;
const FAIR_PERCENT = 50;

function getCompletenessToneClass(percent: number): string {
  if (percent >= HEALTHY_PERCENT) return 'text-success-foreground';
  if (percent >= FAIR_PERCENT) return 'text-warning-foreground';
  return 'text-danger-foreground';
}

interface TeacherProfileCompletenessProps {
  teacher: TeacherDetail;
}

export function TeacherProfileCompleteness({ teacher }: TeacherProfileCompletenessProps) {
  const { percent, missingLabels } = getProfileCompleteness(teacher);
  const unfilledLength = RING_CIRCUMFERENCE * (1 - percent / PERCENT_SCALE);

  return (
    <section aria-label="Profile completeness" className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className={cn('relative size-14 shrink-0', getCompletenessToneClass(percent))}>
          <svg
            viewBox={`0 0 ${RING_VIEWBOX_SIZE} ${RING_VIEWBOX_SIZE}`}
            className="size-full -rotate-90"
            aria-hidden
          >
            <circle
              cx={RING_CENTER}
              cy={RING_CENTER}
              r={RING_RADIUS}
              fill="none"
              stroke="currentColor"
              strokeOpacity={RING_TRACK_OPACITY}
              strokeWidth={RING_STROKE_WIDTH}
            />
            <circle
              cx={RING_CENTER}
              cy={RING_CENTER}
              r={RING_RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth={RING_STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={unfilledLength}
              className="motion-safe:transition-[stroke-dashoffset] motion-safe:duration-500"
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-foreground tabular-nums">
            {percent}%
          </span>
        </div>

        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-sm font-semibold">Profile completeness</p>
          <p className="text-xs text-muted-foreground">
            {missingLabels.length === 0
              ? 'Every detail is filled in.'
              : `Missing ${formatCount(missingLabels.length, 'detail')}`}
          </p>
        </div>
      </div>

      {missingLabels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {missingLabels.map((label) => (
            <span
              key={label}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-medium',
                STATUS_COLOR_CLASS.danger,
              )}
            >
              <X className="size-3 shrink-0" strokeWidth={2.5} aria-hidden />
              {label}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

export function TeacherProfileCompletenessSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden>
      <div className="flex items-center gap-3">
        <Skeleton className="size-14 shrink-0 rounded-full" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        <Skeleton className="h-4 w-16 rounded-full" />
        <Skeleton className="h-4 w-20 rounded-full" />
        <Skeleton className="h-4 w-14 rounded-full" />
      </div>
    </div>
  );
}
